import { inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CanMatchFn, Router } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { filter } from 'rxjs';

import { ProfileService } from '../profile/profile.service';
import { ProjectsQueryService } from '../projects/projects-query.service';
import { firstValueFrom } from '../shared/observable.utils';

/**
 * Checks if the user has permissions for the relevant project under the :projectId path parameter
 * or if they are a site admin actively in exhaustive mode.
 *
 * If not redirects to the "No Project Access" page
 */
export const projectAccessGuard: CanMatchFn = async (_, segments) => {
  // DANGER! canMatch guards run during route matching, before any canActivate guards (including AuthGuard on the parent route).
  // For an unauthenticated user, the profile request below fails with login_required in auth0's interceptor,
  // so profile$ never emits and this guard never resolves. Matching then never completes, AuthGuard never runs,
  // and the user is left on a blank page instead of being sent to login.
  // inject before the first await, the injection context ends there
  const auth0 = inject(Auth0Service);
  const projectService = inject(ProjectsQueryService);
  const profileService = inject(ProfileService);
  const queryClient = inject(QueryClient);
  const router = inject(Router);
  const exhaustiveMode = toSignal(profileService.exhaustiveMode$); // toSignal needs the injection context too

  if (!(await firstValueFrom(auth0.isAuthenticated$))) {
    return true; // AuthGuard sends unauthenticated users to login
  }

  const profile = await firstValueFrom(profileService.profile$.pipe(filter((profile) => Boolean(profile))));

  // !!! just manually grabbing the relvant route segment, might be fragile
  const projectId = segments[1].path;

  const project = await queryClient.ensureQueryData(projectService.getProjectQueryOptions(signal(projectId)));

  const userIsIncludedInProject = project.permissions.find((userWithProjectAccess) => userWithProjectAccess._id === profile._id);

  const hasAccess = Boolean(userIsIncludedInProject) || exhaustiveMode();

  return hasAccess || router.createUrlTree(['projects', projectId, 'no-access']);
};
